import { QueryFunctionContext } from "react-query";
import { BILL_QUERY_LIMIT } from "utils/constants";
import { ADD_NZ_GST } from "utils/currency";
import { JwtClient } from "utils/jwtClient";

import { formatBillingEntityAttributes } from "./billingEntity.utils";

export const getInvoiceTimeline = async (
  endDate: Date,
  customerNumber: string,
  jwtClient: JwtClient,
): Promise<Array<InvoiceSummary>> => {
  const response = await jwtClient.apiCall({
    path: `/billing/customer_billing_entities/${customerNumber}/invoice_timeline`,
    queryParams: {
      profile: "base,internal_only",
      end_at: endDate,
      limit: BILL_QUERY_LIMIT,
      show_payment_transactions: true,
    },
  });
  const { data } = await response.json();
  return sortedInvoiceTimeline(data);
};

export const getInvoice = async ({
  queryKey,
}: QueryFunctionContext<
  [string, { invoiceNumber: string; jwtClient: JwtClient }]
>): Promise<Invoice> => {
  const [_, { invoiceNumber, jwtClient }] = queryKey;
  const response = await jwtClient.apiCall({
    path: `/billing/invoices/${invoiceNumber}`,
    queryParams: {
      invoice_identifier: invoiceNumber,
      group_line_items: true,
      swallow_invoiced_error: true,
      optional_profiles: "with_payments",
    },
  });
  const {
    data: { id: id, attributes: bill },
    included: lineItems,
  } = await response.json();

  return { id, bill, lineItems };
};

export const getLatestReceivableInvoice = async ({
  queryKey,
}: QueryFunctionContext<
  [string, { customerNumber: string; jwtClient: JwtClient }]
>): Promise<ReceivableInvoice> => {
  const [_, { customerNumber, jwtClient }] = queryKey;
  const response = await jwtClient.apiCall({
    path: "/billing/invoices",
    queryParams: {
      customer_identifier: customerNumber,
    },
  });
  const { data } = await response.json();
  return data;
};

export const getInvoiceResourceUrls = async ({
  queryKey,
}: QueryFunctionContext<
  [string, { invoiceUUID: string; jwtClient: JwtClient }]
>): Promise<InvoiceResourceUrls> => {
  const [_, { invoiceUUID, jwtClient }] = queryKey;
  const response = await jwtClient.apiCall({
    path: `/billing/invoices/${invoiceUUID}/resource_urls`,
  });
  const {
    data: { attributes: resource_urls },
  } = await response.json();

  return { resource_urls };
};

export const getBillingEntity = async ({
  queryKey,
}: QueryFunctionContext<
  [string, { customerNumber: string; jwtClient: JwtClient }]
>): Promise<BillingEntity> => {
  const [_, { customerNumber, jwtClient }] = queryKey;
  const response = await jwtClient.apiCall({
    path: `/billing/customer_billing_entities/${customerNumber}`,
  });
  const {
    data: { id, attributes },
  } = await response.json();

  const formattedAttributes = formatBillingEntityAttributes(attributes);
  return { id, ...formattedAttributes };
};

export const getBillingBalance = async ({
  queryKey,
}: QueryFunctionContext<
  [string, { customerNumber: string; jwtClient: JwtClient }]
>): Promise<BillingBalance> => {
  const [_, { customerNumber, jwtClient }] = queryKey;
  const response = await jwtClient.apiCall({
    path: `/billing/balance`,
    queryParams: {
      customer_identifier: customerNumber,
    },
  });
  const {
    data: { attributes },
  } = await response.json();

  return attributes;
};

export const getBillSmootherTransactions = async (
  customerNumber,
  endDate,
  jwtClient,
): Promise<BillSmootherTransaction[]> => {
  const response = await jwtClient.apiCall({
    path: `/billing/customer_transactions`,
    queryParams: {
      customer_id: customerNumber,
      limit: BILL_QUERY_LIMIT,
      end_at: endDate,
      sort: "desc",
      account: "balance",
    },
  });
  const { data } = await response.json();
  return data;
};

export const getOutstandingInvoices = async ({
  queryKey,
}: QueryFunctionContext<
  [
    string,
    {
      customerNumber: string;
      jwtClient: JwtClient;
    },
  ]
>): Promise<OutstandingInvoiceSummary[]> => {
  const [_, { customerNumber, jwtClient }] = queryKey;
  const response = await jwtClient.apiCall({
    path: `/billing/invoices/outstanding_invoices`,
    queryParams: {
      customer_identifier: customerNumber,
    },
  });
  const { data } = await response.json();
  return data;
};

export const getCharges = async ({
  queryKey,
}: QueryFunctionContext<
  [
    string,
    {
      customerNumber: string;
      endAt: string | Date;
      startAt: string | Date;
      supplyNodeRef: string;
      jwtClient: JwtClient;
    },
  ]
>): Promise<Charges> => {
  const [_, { customerNumber, endAt, startAt, supplyNodeRef, jwtClient }] =
    queryKey;
  const response = await jwtClient.apiCall({
    path: `/billing/charges/${customerNumber}`,
    queryParams: {
      customer_identifier: customerNumber,
      end_at: endAt,
      start_at: startAt,
      supply_node_ref: supplyNodeRef,
    },
  });
  const {
    data: { attributes },
  } = await response.json();
  attributes.summed_charges.forEach((item) => (item.total_cost *= ADD_NZ_GST));
  return attributes;
};

const sortedInvoiceTimeline = (bills: InvoiceSummary[]): InvoiceSummary[] => {
  return bills.sort(
    (a, b) =>
      +b.attributes.billing_period.period_started_at -
      +a.attributes.billing_period.period_started_at,
  );
};

export const payOverdueBill = async ({
  invoiceID,
  jwtClient,
  invoiceNumber,
}: {
  invoiceID: string;
  invoiceNumber: string;
  jwtClient: JwtClient;
}): Promise<{ dps_uri: string }> => {
  const response = await jwtClient.apiCall({
    path: `/billing/invoices/${invoiceID}/create_payment`,
    queryParams: {
      redirect_url: `${process.env.REACT_APP_PUBLIC_URL}/bills/${invoiceNumber}/`,
    },
    params: {
      headers: {
        Accept: "application/vnd.flick+json",
        "Content-Type": "application/vnd.flick+json",
      },
    },
  });

  const data = await response.json();
  return data;
};
